<template>
  <div class="payment-wrapper">
    <!-- QR Code View -->
    <Card v-if="!showVerificationForm" class="payment-card-wrapper">
      <template #title>
        <div class="payment-header">
          <h2 class="payment-title">Complete Your Subscription</h2>
          <p class="payment-subtitle">SEBI Registered Investment Adviser | Reg. No: INA000020554</p>
        </div>
      </template>

      <template #content>
        <div class="payment-content">
          <!-- QR Code Section -->
          <div class="qr-section">
            <div class="qr-code-wrapper">
              <qrcode-vue :value="upiValue" :size="240" level="H" render-as="svg" class="qr-code" />
            </div>
            <p class="vpa-display">{{ vpa }}</p>
          </div>

          <!-- Payment Information -->
          <div class="payment-info">
            <div class="info-row">
              <span class="info-label">Payee Name:</span>
              <span class="info-value">{{ payeeName }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Amount:</span>
              <span class="info-value amount">₹{{ displayAmount }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Reference:</span>
              <span class="info-value reference">{{ clientRef }}</span>
            </div>
          </div>

          <!-- Mobile Payment Button -->
          <div class="mobile-payment-section">
            <p class="mobile-hint">On mobile? Pay directly via your UPI app:</p>
            <a :href="upiValue" class="mobile-pay-btn">
              <i class="pi pi-mobile"></i>
              Pay via UPI App
            </a>
          </div>

          <!-- Instructions -->
          <div class="payment-instructions">
            <h4>How to Pay:</h4>
            <ol>
              <li><strong>On Desktop:</strong> Scan the QR code above using any UPI app (GPay, PhonePe, Paytm, etc.)</li>
              <li><strong>On Mobile:</strong> Click the "Pay via UPI App" button above</li>
              <li>Complete the payment in your UPI app</li>
              <li>Note down the UTR/Transaction Reference Number</li>
              <li>Click "I've Paid" below to verify your payment</li>
            </ol>
          </div>

          <!-- Action Buttons -->
          <div class="payment-actions">
            <ANButton
              label="I've Paid - Verify Payment"
              variant="primary"
              icon="pi pi-check-circle"
              size="large"
              @click="showVerificationForm = true"
              class="verify-btn"
            />
          </div>
        </div>
      </template>
    </Card>

    <!-- Verification Form View -->
    <Card v-else class="verification-card-wrapper">
      <template #title>
        <div class="verification-header">
          <h2 class="verification-title">Verify Your Payment</h2>
          <p class="verification-subtitle">Enter your transaction details to complete verification</p>
        </div>
      </template>

      <template #content>
        <div class="verification-content">
          <!-- Success Message -->
          <div v-if="paymentSubmitted" class="success-message">
            <div class="success-icon-wrapper">
              <i class="pi pi-check-circle"></i>
            </div>
            <h3>Payment Details Submitted Successfully!</h3>
            <p>Our team will verify your payment within 24 hours.</p>
            <p class="info-text">You will receive an email confirmation once verified.</p>
          </div>

          <!-- Verification Form -->
          <form v-else @submit.prevent="handleSubmit" class="verification-form">
            <div class="form-group">
              <label for="utr">
                <i class="pi pi-tag label-icon"></i>
                UTR / Transaction Reference Number <span class="required">*</span>
              </label>
              <div class="input-wrapper">
                <i class="pi pi-hashtag input-icon"></i>
                <InputText
                  id="utr"
                  v-model="utr"
                  placeholder="Enter 12-digit UTR number"
                  :class="{ 'p-invalid': errors.utr }"
                  class="custom-input"
                />
              </div>
              <small v-if="errors.utr" class="p-error">
                <i class="pi pi-exclamation-circle"></i> {{ errors.utr }}
              </small>
              <small v-else class="helper-text">
                <i class="pi pi-info-circle"></i> Found in your payment app's transaction history
              </small>
            </div>

            <div class="form-group">
              <label for="amount">
                <i class="pi pi-indian-rupee label-icon"></i>
                Amount Paid <span class="required">*</span>
              </label>
              <div class="input-wrapper">
                <i class="pi pi-wallet input-icon"></i>
                <InputNumber
                  id="amount"
                  v-model="amount"
                  mode="currency"
                  currency="INR"
                  locale="en-IN"
                  class="custom-input"
                />
              </div>
              <small v-if="errors.amount" class="p-error">
                <i class="pi pi-exclamation-circle"></i> {{ errors.amount }}
              </small>
              <small v-else class="helper-text">
                <i class="pi pi-info-circle"></i> Enter the exact amount you paid
              </small>
            </div>

            <div class="form-actions">
              <ANButton
                type="submit"
                label="Submit for Verification"
                icon="pi pi-check"
                variant="primary"
                :loading="isSubmitting"
                class="submit-btn"
              />
              <ANButton
                type="button"
                label="Back to QR Code"
                icon="pi pi-arrow-left"
                variant="secondary"
                @click="showVerificationForm = false"
                class="back-btn"
              />
            </div>
          </form>
        </div>
      </template>
    </Card>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, reactive } from 'vue'
import { useUserStore } from '@/store/user-store'
import QrcodeVue from 'qrcode.vue'
import Card from 'primevue/card'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import ANButton from '@/components/common/AnButton.vue'

const store = useUserStore()

// Toggle between QR code and verification form
const showVerificationForm = ref(false)

// Verification form state
const utr = ref('')
const amount = ref(500)
const isSubmitting = ref(false)
const paymentSubmitted = ref(false)
const errors = reactive({ utr: '', amount: '' })

// Payment Configuration
const vpa = "vivekgupta.ia@validau"
const payeeName = "VIVEK GUPTA"
const paymentAmount = 500.00
const clientRef = computed(() => {
  const customerId = store.dashboardData?.customerId || 'NEW'
  return `AN-${customerId}-${new Date().getFullYear()}`
})

const displayAmount = computed(() => paymentAmount.toLocaleString('en-IN'))

// UPI Payment URI
const upiValue = computed(() => {
  return `upi://pay?pa=${vpa}&pn=${encodeURIComponent(payeeName)}&am=${paymentAmount.toFixed(2)}&tn=${encodeURIComponent(clientRef.value)}&cu=INR`
})

// Verification form methods
const validateForm = () => {
  errors.utr = !utr.value || utr.value.length < 12 ? 'Enter valid 12-digit UTR' : ''
  errors.amount = !amount.value || amount.value <= 0 ? 'Enter valid amount' : ''
  return !errors.utr && !errors.amount
}

const handleSubmit = async () => {
  if (!validateForm()) return

  isSubmitting.value = true
  try {
    await store.updateCustomerPaymentStatus({
      UTR: utr.value.trim(),
      Amount: amount.value,
      PaymentStatus: 'Paid'
    })
    paymentSubmitted.value = true
    await store.loadDashboardData(store.user.email)
    setTimeout(() => store.setTabView('PaymentStatus'), 2000)
  } catch (error) {
    store.fireAlert('Error', 'Failed to submit. Please try again.')
  } finally {
    isSubmitting.value = false
  }
}

onMounted(() => {
  document.title = 'Complete Payment - AssetNivesh'
})
</script>

<style scoped>
.payment-wrapper {
  padding: 24px;
  max-width: 700px;
  margin: 0 auto;
}

.payment-card-wrapper,
.verification-card-wrapper {
  box-shadow: var(--an-card-shadow);
}

.verification-header {
  text-align: center;
  padding-bottom: 8px;
}

.payment-title,
.verification-title {
  color: var(--an-text-primary);
  margin: 0 0 16px 0;
  font-size: 1.8rem;
  font-weight: 700;
  line-height: 1.3;
}

.payment-subtitle,
.verification-subtitle {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.5;
}

.payment-subtitle {
  color: #28a745;
  margin-bottom: 4px;
}

.verification-subtitle {
  color: #6c757d;
  margin-bottom: 8px;
}

.payment-content,
.verification-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* QR Code Section */
.qr-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  border: 2px solid #dee2e6;
}

.qr-code-wrapper {
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.qr-code {
  display: block;
}

.vpa-display {
  font-family: 'Courier New', monospace;
  color: #495057;
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0;
}

/* Payment Information */
.payment-info {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid var(--an-primary);
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-label {
  color: var(--an-text-secondary);
  font-weight: 500;
  font-size: 0.95rem;
}

.info-value {
  color: var(--an-text-primary);
  font-weight: 600;
  font-size: 1rem;
}

.info-value.amount {
  color: #28a745;
  font-size: 1.3rem;
}

.info-value.reference {
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
}

/* Mobile Payment Section */
.mobile-payment-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-radius: 8px;
}

.mobile-hint {
  color: #1976d2;
  font-weight: 600;
  margin: 0;
  font-size: 0.95rem;
}

.mobile-pay-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #28a745;
  color: white;
  padding: 14px 28px;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
}

.mobile-pay-btn:hover {
  background: #218838;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.4);
}

.mobile-pay-btn i {
  font-size: 1.2rem;
}

/* Instructions */
.payment-instructions {
  padding: 20px;
  background: #fff3cd;
  border-radius: 8px;
  border-left: 4px solid #ffc107;
}

.payment-instructions h4 {
  color: #856404;
  margin: 0 0 12px 0;
  font-size: 1.1rem;
}

.payment-instructions ol {
  margin: 0;
  padding-left: 20px;
  color: #856404;
}

.payment-instructions li {
  margin-bottom: 8px;
  line-height: 1.6;
}

.payment-instructions strong {
  color: #533f03;
}

/* Action Buttons */
.payment-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
}

.verify-btn {
  width: 100%;
}

/* Verification Form Styles */
.verification-content {
  gap: 24px;
}

.verification-form {
  display: flex;
  flex-direction: column;
  gap: 32px;
  max-width: 550px;
  margin: 0 auto;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-group label {
  font-weight: 600;
  color: #2c3e50;
  font-size: 1.05rem;
  display: flex;
  align-items: center;
  gap: 10px;
}

.label-icon {
  color: #667eea;
  font-size: 1.15rem;
}

.required {
  color: #e74c3c;
  font-weight: 700;
  margin-left: 2px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 18px;
  color: #95a5a6;
  font-size: 1.15rem;
  z-index: 1;
}

.form-group :deep(.p-inputtext),
.form-group :deep(.p-inputnumber-input) {
  padding: 16px 18px 16px 52px;
  border: 2px solid #e0e3e7;
  border-radius: 10px;
  font-size: 1.05rem;
  transition: all 0.3s ease;
  width: 100%;
  background: #fafbfc;
  font-weight: 500;
}

.form-group :deep(.p-inputtext:hover),
.form-group :deep(.p-inputnumber-input:hover) {
  border-color: #cbd5e0;
  background: white;
}

.form-group :deep(.p-inputtext:focus),
.form-group :deep(.p-inputnumber-input:focus) {
  border-color: #667eea;
  box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.12);
  outline: none;
  background: white;
}

.form-group :deep(.p-invalid) {
  border-color: #e74c3c;
  background: #fff5f5;
}

.form-group :deep(.p-error) {
  color: #e74c3c;
  font-size: 0.9rem;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.helper-text {
  color: #6c757d;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}

.helper-text i {
  color: #3498db;
  font-size: 0.95rem;
}

.form-actions {
  display: flex;
  gap: 16px;
  margin-top: 24px;
}

.form-actions :deep(button) {
  flex: 1;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.3s ease;
  min-height: 44px;
}

.form-actions :deep(.submit-btn) {
  background: var(--an-primary);
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-actions :deep(.submit-btn:hover:not(:disabled)) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
}

.form-actions :deep(.back-btn) {
  background: white;
  color: #28a745;
  border: 2px solid #28a745;
}

.form-actions :deep(.back-btn:hover) {
  background: #28a745;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.success-message {
  text-align: center;
  padding: 56px 40px;
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  border-radius: 12px;
  border: 2px solid #28a745;
  max-width: 550px;
  margin: 0 auto;
}

.success-icon-wrapper {
  width: 90px;
  height: 90px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  box-shadow: 0 4px 20px rgba(40, 167, 69, 0.25);
}

.success-message i {
  font-size: 4rem;
  color: #28a745;
  animation: checkmark-pop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes checkmark-pop {
  0% { transform: scale(0) rotate(0deg); }
  50% { transform: scale(1.2) rotate(10deg); }
  100% { transform: scale(1) rotate(0deg); }
}

.success-message h3 {
  color: #155724;
  margin: 0 0 16px 0;
  font-size: 1.6rem;
  font-weight: 700;
}

.success-message p {
  color: #155724;
  font-size: 1.05rem;
  margin: 0 0 10px 0;
  font-weight: 500;
  line-height: 1.5;
}

.info-text {
  color: #6c757d !important;
  font-size: 0.95rem !important;
  font-weight: 400 !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .payment-wrapper {
    padding: 12px;
  }

  .payment-title,
  .verification-title {
    font-size: 1.4rem;
    margin: 0 0 12px 0;
  }

  .payment-subtitle,
  .verification-subtitle {
    font-size: 0.85rem;
    margin-bottom: 6px;
  }

  .qr-code-wrapper :deep(svg) {
    width: 200px !important;
    height: 200px !important;
  }

  .payment-instructions {
    font-size: 0.85rem;
    padding: 16px;
  }

  .verification-form {
    max-width: 100%;
    gap: 24px;
  }

  .form-group {
    gap: 10px;
  }

  .form-group label {
    font-size: 0.95rem;
    gap: 8px;
  }

  .label-icon {
    font-size: 1rem;
  }

  .input-icon {
    left: 14px;
    font-size: 1rem;
  }

  .form-group :deep(.p-inputtext),
  .form-group :deep(.p-inputnumber-input) {
    padding: 14px 14px 14px 46px;
    font-size: 0.95rem;
    border-radius: 8px;
  }

  .form-group :deep(.p-error),
  .helper-text {
    font-size: 0.8rem;
  }

  .form-actions {
    flex-direction: column;
    gap: 12px;
    margin-top: 16px;
  }

  .form-actions :deep(button) {
    width: 100%;
    padding: 12px 20px;
    font-size: 0.95rem;
    min-height: 44px;
  }

  .success-message {
    padding: 40px 24px;
  }

  .success-icon-wrapper {
    width: 70px;
    height: 70px;
  }

  .success-message i {
    font-size: 3rem;
  }

  .success-message h3 {
    font-size: 1.3rem;
  }

  .success-message p {
    font-size: 0.95rem;
  }

  .info-text {
    font-size: 0.85rem !important;
  }
}

@media (max-width: 480px) {
  .payment-wrapper {
    padding: 8px;
  }

  .payment-title,
  .verification-title {
    font-size: 1.25rem;
    margin: 0 0 10px 0;
  }

  .payment-subtitle,
  .verification-subtitle {
    font-size: 0.8rem;
    margin-bottom: 4px;
  }

  .verification-form {
    gap: 20px;
  }

  .form-group {
    gap: 8px;
  }

  .form-group label {
    font-size: 0.9rem;
    flex-wrap: wrap;
  }

  .form-group :deep(.p-inputtext),
  .form-group :deep(.p-inputnumber-input) {
    padding: 12px 12px 12px 42px;
    font-size: 0.9rem;
  }

  .form-actions :deep(button) {
    padding: 11px 16px;
    font-size: 0.9rem;
    min-height: 42px;
  }

  .success-message {
    padding: 32px 20px;
  }

  .success-icon-wrapper {
    width: 60px;
    height: 60px;
  }

  .success-message i {
    font-size: 2.5rem;
  }

  .success-message h3 {
    font-size: 1.2rem;
  }

  .success-message p {
    font-size: 0.9rem;
  }
}

/* Tablet landscape */
@media (min-width: 769px) and (max-width: 1024px) {
  .payment-wrapper {
    max-width: 650px;
  }

  .verification-form {
    max-width: 500px;
  }

  .form-group label {
    font-size: 1rem;
  }

  .form-group :deep(.p-inputtext),
  .form-group :deep(.p-inputnumber-input) {
    padding: 14px 16px 14px 50px;
    font-size: 1rem;
  }

  .form-actions :deep(button) {
    padding: 13px 24px;
    font-size: 1rem;
    min-height: 46px;
  }
}
</style>
